import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextProps {
    theme: Theme;
    toggleTheme: () => void;
    effectiveTheme: 'light' | 'dark'; // The actual applied theme (never 'system')
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [theme, setTheme] = useState<Theme>(() => {
        const storedTheme = localStorage.getItem('theme') as Theme | null;
        // If no theme stored, default to system
        return storedTheme || 'system';
    });

    // Track the effective theme (what's actually applied)
    const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(() => {
        // Initialize effective theme based on system preference
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        return prefersDark ? 'dark' : 'light';
    });

    // Sync root class to current theme (no longer auto-writing localStorage here)
    useEffect(() => {
        const root = window.document.documentElement;
        // Apply effective theme rather than selected theme
        if (effectiveTheme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }, [effectiveTheme]);

    // Update effective theme when theme or system preference changes
    useEffect(() => {
        if (theme === 'system') {
            const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
            setEffectiveTheme(prefersDark ? 'dark' : 'light');
        } else {
            setEffectiveTheme(theme);
        }
    }, [theme]);

    // Listen for system color-scheme changes and update theme if not manually set
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleSystemChange = (e: MediaQueryListEvent) => {
            // Only update effective theme if we're in system mode
            if (theme === 'system') {
                setEffectiveTheme(e.matches ? 'dark' : 'light');
            }
        };
        mediaQuery.addEventListener('change', handleSystemChange);
        return () => mediaQuery.removeEventListener('change', handleSystemChange);
    }, [theme]);

    // Toggle theme manually and persist choice
    const toggleTheme = () => {
        setTheme((prevTheme) => {
            // Cycle through light -> dark -> system
            let newTheme: Theme;
            if (prevTheme === 'light') newTheme = 'dark';
            else if (prevTheme === 'dark') newTheme = 'system';
            else newTheme = 'light';

            localStorage.setItem('theme', newTheme);
            return newTheme;
        });
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, effectiveTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextProps => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
